import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Icon, Pagination } from "../";
import { useBusinesses } from "@finitless/shared/hooks";
import { useApiStore, useUserStore } from "@finitless/shared/stores";
import { appConfig } from "@finitless/shared/config";
import { useTranslation } from "react-i18next";

/**
 * Generic menu import modal — URL input + business multi-select.
 * Same layout for all platforms (DoorDash, Uber Eats, etc.).
 * Platform-specific: `validateForm`, import hook, and `buildImportParams(form, …)` come from config.
 */
const MenuImportModal = ({ isOpen, onClose, config = {} }) => {
  const {
    titleKey,
    subtitleKey,
    urlLabelKey,
    urlPlaceholderKey,
    urlHelperKey,
    trackerLabelKey,
    trackerPlaceholderKey,
    trackerHelperKey,
    orderingProjectLabelKey,
    orderingProjectPlaceholderKey,
    orderingBusinessSegmentLabelKey,
    orderingBusinessSegmentPlaceholderKey,
    orderingStructuredHintKey,
    statusImportingKey,
    includeAllMenuVariantsKey,
    validateForm,
    useImport,
    buildImportParams,
  } = config;
  const { t } = useTranslation("menuImporters");
  const navigate = useNavigate();
  const { triggerImport, isImporting } = useImport();
  const project = useApiStore((state) => state.project);
  const orderingApiBaseUrl = appConfig?.api?.url || "";
  const user = useUserStore((state) => state.user);

  const [urlInput, setUrlInput] = useState("");
  const [trackerInput, setTrackerInput] = useState("");
  const [orderingPlusProjectId, setOrderingPlusProjectId] = useState("");
  const [orderingBusinessSegment, setOrderingBusinessSegment] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBusinesses, setSelectedBusinesses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [includeAllMenuVariants, setIncludeAllMenuVariants] = useState(true);
  const [importSuccess, setImportSuccess] = useState(null);

  const pageSize = 10;

  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const { data, isLoading, error, refetch } = useBusinesses({
    enabledFilter: "all",
    searchTerm,
    page: currentPage,
    pageSize,
  });

  const businesses = data?.businesses || [];
  const pagination = data?.pagination || {};
  const importForm = useMemo(
    () => ({
      url: urlInput,
      tracker: trackerInput,
      orderingPlusProjectId,
      orderingBusinessSegment,
    }),
    [urlInput, trackerInput, orderingPlusProjectId, orderingBusinessSegment],
  );
  const formValidation = useMemo(
    () => validateForm(importForm),
    [importForm, validateForm],
  );
  const showFormError =
    Boolean(formValidation.error) &&
    (urlInput.trim() !== "" ||
      trackerInput.trim() !== "" ||
      orderingPlusProjectId.trim() !== "" ||
      orderingBusinessSegment.trim() !== "");
  const isImportEnabled = formValidation.valid && selectedBusinesses.length > 0;
  const hasOrderingStructuredFields = Boolean(orderingProjectLabelKey);
  const hasTrackerField = Boolean(trackerLabelKey);

  useEffect(() => {
    if (!isOpen) {
      setUrlInput("");
      setTrackerInput("");
      setOrderingPlusProjectId("");
      setOrderingBusinessSegment("");
      setSearchInput("");
      setSearchTerm("");
      setSelectedBusinesses([]);
      setCurrentPage(1);
      setIncludeAllMenuVariants(true);
      setImportSuccess(null);
    }
  }, [isOpen]);

  const handleImport = () => {
    if (!isImportEnabled || isImporting) return;
    const businessIds = selectedBusinesses.map((b) => b.id);
    const params = buildImportParams(
      importForm,
      businessIds,
      project,
      orderingApiBaseUrl,
      includeAllMenuVariants,
      user?.email,
    );
    triggerImport(params, {
      onSuccess: () => {
        setImportSuccess({ selectedBusinesses: [...selectedBusinesses] });
      },
    });
  };

  const handleGoToProducts = () => {
    if (
      importSuccess?.selectedBusinesses?.length === 1 &&
      importSuccess.selectedBusinesses[0].slug
    ) {
      navigate(`/menu/products/${importSuccess.selectedBusinesses[0].slug}`);
    } else {
      navigate("/menu/products");
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={isImporting ? undefined : onClose}
      title={t(titleKey)}
      subtitle={t(subtitleKey)}
      maxWidth="max-w-2xl"
      className="sm:max-h-[80vh]"
    >
      <div className="relative space-y-6">
        {importSuccess ? (
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-300">
              {importSuccess.selectedBusinesses.length <= 1
                ? t("status.success")
                : t("status.successToStores", {
                    count: importSuccess.selectedBusinesses.length,
                  })}
            </p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleGoToProducts}
                className="rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:from-blue-700 hover:to-purple-700"
              >
                {importSuccess.selectedBusinesses.length === 1 &&
                importSuccess.selectedBusinesses[0].slug
                  ? t("actions.goToStoreProducts", {
                      storeName:
                        importSuccess.selectedBusinesses[0].name ||
                        t("actions.thisStore"),
                    })
                  : t("actions.goToProducts")}
              </button>
            </div>
          </div>
        ) : isImporting ? (
          <div
            className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg bg-gray-950/80 backdrop-blur-sm"
            aria-live="polite"
          >
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
            <p className="mt-3 text-sm text-gray-300">
              {t(statusImportingKey)}
            </p>
          </div>
        ) : null}

        {!importSuccess && (
          <>
            <div>
              <label
                htmlFor="menu-import-url-input"
                className="mb-1.5 block text-sm font-medium text-gray-300"
              >
                {t(urlLabelKey)}
              </label>
              <input
                id="menu-import-url-input"
                type="text"
                inputMode="url"
                autoComplete="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder={t(urlPlaceholderKey)}
                aria-describedby={
                  showFormError ? "url-helper form-import-error" : "url-helper"
                }
                aria-invalid={showFormError && urlInput.trim() !== ""}
                className="h-10 w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
              <p id="url-helper" className="mt-1 text-xs text-gray-400">
                {t(urlHelperKey)}
              </p>
            </div>

            {hasTrackerField && (
              <div>
                <label
                  htmlFor="menu-import-tracker-input"
                  className="mb-1.5 block text-sm font-medium text-gray-300"
                >
                  {t(trackerLabelKey)}
                </label>
                <input
                  id="menu-import-tracker-input"
                  type="text"
                  autoComplete="off"
                  value={trackerInput}
                  onChange={(e) => setTrackerInput(e.target.value)}
                  placeholder={t(trackerPlaceholderKey)}
                  aria-describedby="tracker-helper"
                  className="h-10 w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 font-mono text-sm text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
                <p id="tracker-helper" className="mt-1 text-xs text-gray-400">
                  {t(trackerHelperKey)}
                </p>
              </div>
            )}

            {hasOrderingStructuredFields && (
              <div className="space-y-4">
                <p className="text-xs text-gray-500">{t("modal.orderingOr")}</p>
                <div>
                  <label
                    htmlFor="menu-import-ordering-project"
                    className="mb-1.5 block text-sm font-medium text-gray-300"
                  >
                    {t(orderingProjectLabelKey)}
                  </label>
                  <input
                    id="menu-import-ordering-project"
                    type="text"
                    autoComplete="off"
                    value={orderingPlusProjectId}
                    onChange={(e) => setOrderingPlusProjectId(e.target.value)}
                    placeholder={t(orderingProjectPlaceholderKey)}
                    className="h-10 w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label
                    htmlFor="menu-import-ordering-segment"
                    className="mb-1.5 block text-sm font-medium text-gray-300"
                  >
                    {t(orderingBusinessSegmentLabelKey)}
                  </label>
                  <input
                    id="menu-import-ordering-segment"
                    type="text"
                    autoComplete="off"
                    value={orderingBusinessSegment}
                    onChange={(e) => setOrderingBusinessSegment(e.target.value)}
                    placeholder={t(orderingBusinessSegmentPlaceholderKey)}
                    className="h-10 w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <p className="text-xs text-gray-400">
                  {t(orderingStructuredHintKey)}
                </p>
              </div>
            )}

            {showFormError && (
              <p
                id="form-import-error"
                className="text-xs text-red-400"
                role="alert"
              >
                {t(formValidation.error)}
              </p>
            )}

            {includeAllMenuVariantsKey && (
              <div className="flex items-center gap-3">
                <input
                  id="menu-import-include-variants"
                  type="checkbox"
                  checked={includeAllMenuVariants}
                  onChange={(e) => setIncludeAllMenuVariants(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 focus:ring-offset-gray-950"
                />
                <label
                  htmlFor="menu-import-include-variants"
                  className="cursor-pointer text-sm text-gray-300"
                >
                  {t(includeAllMenuVariantsKey)}
                </label>
              </div>
            )}

            <div>
              <div className="mb-3 flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-300">
                  {t("modal.businessesLabel")}
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedBusinesses((prev) => {
                        const byId = new Map(prev.map((b) => [b.id, b]));
                        businesses.forEach((b) =>
                          byId.set(b.id, {
                            id: b.id,
                            slug: b.slug,
                            name: b.name,
                          }),
                        );
                        return Array.from(byId.values());
                      })
                    }
                    className="rounded bg-blue-500/10 px-2 py-1 text-xs text-blue-400 transition-colors hover:bg-blue-500/20"
                  >
                    {t("modal.selectAll")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedBusinesses([])}
                    className="rounded bg-gray-800 px-2 py-1 text-xs text-gray-400 transition-colors hover:bg-gray-700 hover:text-white"
                  >
                    {t("modal.clear")}
                  </button>
                </div>
              </div>

              <div className="relative mb-3">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Icon name="Search" className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder={t("modal.businessSearchPlaceholder")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="h-10 w-full rounded-lg border border-gray-700 bg-gray-800 py-2 pr-4 pl-10 text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              {isLoading ? (
                <div className="grid grid-cols-1 gap-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="animate-pulse rounded-lg border border-gray-800 bg-gray-900 p-4"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-5 w-5 rounded border border-gray-700 bg-gray-800" />
                        <div className="h-10 w-10 rounded-lg bg-gray-800" />
                        <div className="flex-1">
                          <div className="mb-1 h-4 w-32 rounded bg-gray-800" />
                          <div className="h-3 w-20 rounded bg-gray-800" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : error ? (
                <div className="rounded-lg border border-red-500/30 bg-red-900/20 p-4">
                  <p className="text-sm text-red-400">
                    {error.message || t("status.error")}
                  </p>
                  <button
                    onClick={() => refetch()}
                    className="mt-2 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
                  >
                    {t("actions.retry")}
                  </button>
                </div>
              ) : businesses.length === 0 ? (
                <div className="rounded-lg border border-gray-800 bg-gray-900 p-6 text-center">
                  <p className="text-sm text-gray-400">{t("empty.noStores")}</p>
                </div>
              ) : (
                <>
                  <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-gray-800 bg-gray-900 p-2">
                    {businesses.map((business) => {
                      const isSelected = selectedBusinesses.some(
                        (b) => b.id === business.id,
                      );
                      return (
                        <button
                          key={business.id}
                          type="button"
                          onClick={() =>
                            setSelectedBusinesses((prev) =>
                              prev.some((b) => b.id === business.id)
                                ? prev.filter((b) => b.id !== business.id)
                                : [
                                    ...prev,
                                    {
                                      id: business.id,
                                      slug: business.slug,
                                      name: business.name,
                                    },
                                  ],
                            )
                          }
                          className={`flex w-full cursor-pointer items-center gap-3 rounded-lg p-3 text-left transition-colors ${
                            isSelected
                              ? "bg-blue-600/10 hover:bg-blue-600/20"
                              : "hover:bg-gray-800"
                          }`}
                        >
                          <div
                            className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border transition-colors ${
                              isSelected
                                ? "border-blue-500 bg-blue-500"
                                : "border-gray-600 bg-gray-950"
                            }`}
                          >
                            {isSelected && (
                              <Icon
                                name="Check"
                                className="h-3 w-3 text-white"
                              />
                            )}
                          </div>
                          {business.logo ? (
                            <img
                              src={business.logo}
                              alt=""
                              className="h-10 w-10 flex-shrink-0 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-gray-700 bg-gray-800">
                              <Icon
                                name="Store"
                                className="h-5 w-5 text-gray-400"
                              />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-white">
                              {business.name}
                            </p>
                            {(business.city?.name ||
                              (typeof business.city === "string" &&
                                business.city)) && (
                              <p className="truncate text-xs text-gray-400">
                                {business.city?.name || business.city}
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {pagination?.total_pages > 1 && (
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-sm text-gray-400">
                        {t("pagination.showing", {
                          from: pagination.from,
                          to: pagination.to,
                          total: pagination.total,
                        })}
                      </span>
                      <Pagination
                        currentPage={currentPage}
                        totalPages={pagination.total_pages}
                        onPageChange={setCurrentPage}
                        maxVisiblePages={5}
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-800 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700"
              >
                {t("modal.cancelButton")}
              </button>
              <button
                type="button"
                onClick={handleImport}
                disabled={!isImportEnabled || isImporting}
                className="rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:from-blue-700 hover:to-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t("modal.importButton")}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default MenuImportModal;
